import { inject, Injectable } from '@angular/core';
import type { Params, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PagePath } from '../enums/page-path.enum';
import { type MedicarePlanIds, PassParameterPages } from '../models/deep-linking.model';
import type { PassParams, PassParamsMap } from '../models/pass-parameter.model';
import { MEDICARE_PLAN_TYPES, type Plan } from '../models/plan.model';
import { SpinnerEvent } from '../models/spinner.model';
import { ConfigResourceService } from './config-resource.service';
import { DemographicsStateService } from './demographics-state.service';
import { NavigationService } from './navigation.service';
import { PlansStateService } from './plans-state.service';
import { PlansService } from './plans.service';
import { SpinnerService } from './spinner.service';

@Injectable({ providedIn: 'root' })
export class DeepLinkingService {
  private readonly plansService = inject(PlansService);
  private readonly plansStateService = inject(PlansStateService);
  private readonly demographicsStateService = inject(DemographicsStateService);
  private readonly navigationService = inject(NavigationService);
  private readonly configService = inject(ConfigResourceService);
  private readonly spinnerService = inject(SpinnerService);

  private readonly DEFAULT_PLAN_SEGMENT: string = '000';

  getAllowablePages(): PagePath[] {
    return PassParameterPages;
  }

  async processPageSpecificParams(pagePath: string | null, passParametersMap: PassParamsMap): Promise<UrlTree | null> {
    if (passParametersMap?.deepLinkingPassParams) {
      return await this.loadPageSpecificParams(pagePath, passParametersMap.deepLinkingPassParams);
    }
    return null;
  }

  redirectToHome(queryParams?: Params): UrlTree {
    return this.navigationService.constructUrlTreeFromPagePath(PagePath.HOME, { queryParams });
  }

  redirectToPageTag(pagePath: PagePath, queryParams?: Params): UrlTree {
    return this.navigationService.constructUrlTreeFromPagePath(pagePath, { queryParams });
  }

  isDestinationPageAllowed(pagePaths: string[], queryParams: Params): boolean {
    const isDeepLinkingEnabled: boolean =
      this.configService.getProcessedResourceValue<string>('MDC6IsDeepLinkingEnabled', 'true')?.toLowerCase() === 'true';

    // Redirect if deep linking is disabled, and has query parameters
    if (!isDeepLinkingEnabled && !!queryParams) {
      const allowedPages = this.getAllowablePages();
      return !!pagePaths?.length && allowedPages.some(pageName => pagePaths.includes(pageName));
    }

    return true;
  }

  // There are now certain pages that we do not wish to include plan data on since
  // we are loading per plan type. If a plan type is provided then we return plan
  // data because they are explicitly opting in to that planType but otherwise
  // we will defer to the page to load plans if its included in this list.
  includePlanData(params: PassParams, pagePaths: string[]): boolean {
    return !!params?.PlanType || pagePaths?.includes(PagePath.SELECT_PLAN) || pagePaths?.includes(PagePath.ACCOUNT_PRESCRIPTIONS);
  }

  getPlansOfParams(planIds: string[]): Plan[] {
    return this.isMedicarePlanIdFormat(planIds) ? this.getMedicarePlansOfParams(planIds) : this.getPlanOfParams(planIds);
  }

  resolvePlanIDs(planIDs: string): string[] {
    const planIdsArray = planIDs ? planIDs.split(',') : [];
    return planIdsArray.map(planId => planId.trim());
  }

  private async loadPageSpecificParams(pagePath: string | null, params: PassParams): Promise<UrlTree | null> {
    if (params) {
      if (pagePath?.includes(PagePath.SELECT_PLAN)) {
        return await this.loadSelectPlansPage(params);
      } else if (pagePath?.includes(PagePath.HOME) || pagePath?.includes(PagePath.ACCOUNT_PRESCRIPTIONS)) {
        return await this.loadPrescriptionsPage(params);
      }
    }
    return null;
  }

  private async loadSelectPlansPage(params: PassParams): Promise<UrlTree | null> {
    if (this.demographicsStateService.hasSelectedZipAndFips()) {
      if (!this.plansStateService.hasPlansForSelectedDemographics()) {
        const zip: string = this.demographicsStateService.getZip();
        const fips: string = this.demographicsStateService.getFips();

        if (zip && fips) {
          const loaderText: string = this.spinnerService.getLoaderMessage(PagePath.SELECT_PLAN);
          this.spinnerService.show({ event: SpinnerEvent.PLANS_LOADING, loaderText });

          try {
            await firstValueFrom(this.plansStateService.loadPlans$(zip, fips));
            return this.redirectToPageTag(PagePath.SELECT_PLAN, params);
          } catch (error) {
            console.error('Error loading plans and redirecting to Select Plan page:', error);
          } finally {
            this.spinnerService.hide({ event: SpinnerEvent.PLANS_LOADING });
          }
        }
      }

      // Redirect to Select Plans page directly when we already have plans for the selected demographics (Zip/FIPS)
      return this.redirectToPageTag(PagePath.SELECT_PLAN, params);
    }

    return null;
  }

  private async loadPrescriptionsPage(params: PassParams): Promise<UrlTree | null> {
    if (this.demographicsStateService.hasSelectedZipAndFips()) {
      const selectPlanUrlTree: UrlTree | null = await this.loadSelectPlansPage(params);
      const { Plan: plans, PlanId: planId } = params ?? {};

      if (plans || planId) {
        let planIds: string[] = [];

        if (plans) {
          planIds = this.resolvePlanIDs(plans);
        } else if (planId) {
          planIds.push(planId);
        }

        if (planIds.length) {
          const plansInParam: Plan[] = this.getPlansOfParams(planIds);

          if (plansInParam.length) {
            await firstValueFrom(this.plansStateService.selectPlan$(plansInParam[0].id));
            return this.redirectToPageTag(PagePath.ACCOUNT_PRESCRIPTIONS, params);
          }
        }
      }

      return selectPlanUrlTree;
    }

    return null;
  }

  private getMedicarePlansOfParams(medicarePlanIds: string[]): Plan[] {
    const medicarePlanIdsFragments: MedicarePlanIds[] = medicarePlanIds.map<MedicarePlanIds>(medicarePlanId => {
      const [contractId, planId, segmentId = this.DEFAULT_PLAN_SEGMENT] = medicarePlanId.split('-');

      return { contractId, planId, segmentId };
    });

    return this.plansStateService
      .getAllPlans()
      .filter(
        plan =>
          MEDICARE_PLAN_TYPES.some(medicarePlanType => plan.planTypeAbbreviation === medicarePlanType) &&
          medicarePlanIdsFragments.some(
            ({ contractId, planId, segmentId }) => plan.contractID === contractId && plan.planID === planId && plan.segmentID === segmentId
          )
      );
  }

  private getPlanOfParams(planIds: string[]): Plan[] {
    return this.plansStateService
      .getAllPlans()
      .filter(
        plan => MEDICARE_PLAN_TYPES.some(medicarePlanType => plan.planTypeAbbreviation === medicarePlanType) && planIds.includes(plan.id)
      );
  }

  private isMedicarePlanIdFormat(planIds: string[]): boolean {
    // for deep linking, medicare plan IDs have the following format: H0523-061-000
    // H0523: contract ID
    // 061: plan ID
    // 000: segment ID
    return planIds.some(planId => planId.includes('-'));
  }
}
